import { useEffect, useRef, useState } from 'react';
import { EventEmitter } from 'eventemitter3';

import { ConfirmModal, ConfirmModalProps } from './ConfirmModal.component';

interface ConfirmModalOpenArgs {
  modalProps?: Partial<ModalProps>;
}

interface ConfirmModalEventTypes {
  close: (confirm?: boolean) => void;
  open: (args?: ConfirmModalOpenArgs) => void;
}

export type ModalProps = Omit<ConfirmModalProps, 'isVisible' | 'onConfirm' | 'onCancel' | 'onCloseDialog'>;

/**
 * Shows a confirm modal.
 *
 * Usage:
 *
 * ```tsx
 const [ConfirmModal, showModal] = useConfirmModal();

 return (
 <Button
 onClick={async () => {
     const result = await showModal();
     console.log(result) // <- true/false/undefined
   }}
 >
 Show Modal
 </Button>
 <ConfirmModal title="..." {...otherProps} />
 );
 ```
 * You can also use the third return value, `hideModal` function
 *
 * @returns [Modal component, showModal function, hideModal function]
 */
export function useConfirmModal() {
  const emitter = useRef(new EventEmitter<ConfirmModalEventTypes>());
  const Modal = useRef(function Modal(props: ModalProps) {
    const [isVisible, setIsVisible] = useState(false);
    const [customProps, setCustomProps] = useState<Partial<ModalProps>>({});

    useEffect(() => {
      const openListener = (args?: ConfirmModalOpenArgs) => {
        setIsVisible(true);
        setCustomProps(args?.modalProps || {});
      };

      const closeListener = () => {
        setIsVisible(false);
        setCustomProps({});
      };

      emitter.current.addListener('open', openListener);
      emitter.current.addListener('close', closeListener);

      return () => {
        emitter.current.removeListener('open', openListener);
        emitter.current.removeListener('close', closeListener);
      };
    }, []);

    if (!isVisible) {
      return null;
    }

    return (
      <ConfirmModal
        {...props}
        {...customProps}
        isVisible={isVisible}
        onConfirm={() => emitter.current.emit('close', true)}
        onCancel={() => emitter.current.emit('close', false)}
        onCloseDialog={() => emitter.current.emit('close', false)}
      />
    );
  });

  const showModal = useRef(async (args?: ConfirmModalOpenArgs) => {
    emitter.current.emit('open', args);

    return new Promise<boolean | undefined>((resolve) => {
      const closeListener = (confirm?: boolean) => {
        emitter.current.removeListener('close', closeListener);

        resolve(confirm);
      };

      emitter.current.addListener('close', closeListener);
    });
  });

  const hideModal = useRef(async () => {
    emitter.current.emit('close');
  });

  return [Modal.current, showModal.current, hideModal.current] as const;
}
